import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import ReviewsCarousel from '../components/ReviewsCarousel';
import ContactForm from '../components/ContactForm';
import Features from '../components/Features';
import SEO, { orgJsonLd, websiteJsonLd } from '../components/SEO';
import { useSiteSettings } from '../hooks/useSiteSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Home() {
  const [popularProducts, setPopularProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { get } = useSiteSettings();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/homepage-data`)
      .then(res => res.json())
      .then(data => {
        const sorted = (data.popular_products || []).sort((a, b) => (a.popular_order || 99) - (b.popular_order || 99));
        setPopularProducts(sorted.slice(0, 4));
        setReviews(data.reviews || []);
      })
      .catch(err => console.error('Error fetching homepage data:', err));
  }, []);

  const categories = [
    { name: 'Peptides', slug: 'peptides' },
    { name: 'Anabolic Steroids', slug: 'anabolic-steroids' },
    { name: 'SARMs and related compounds', slug: 'sarms-and-related-compounds' },
    { name: 'SERMs and related compounds', slug: 'serms-and-related-compounds' },
    { name: 'Sexual health', slug: 'sexual-health' },
    { name: 'Harm reduction', slug: 'harm-reduction' },
    { name: 'Others', slug: 'others' }
  ];

  return (
    <div className="home-page" data-testid="home-page">
      <SEO
        title="Peptide Purity Testing & Anabolic Steroid Lab Analysis"
        noBrand
        description="Kovar Analytics is an independent testing laboratory specialising in peptide purity analysis, anabolic steroid testing, SARMs and pharmaceutical compound identification. HPLC-based identification, quantification and purity. Worldwide shipping, five-business-day turnaround."
        path="/"
        keywords="peptide purity testing, peptide testing lab, peptide analysis laboratory, anabolic steroid testing, steroid purity test, HPLC peptide analysis, semaglutide testing, tirzepatide testing, BPC-157 testing, TB-500 analysis, harm reduction lab"
        jsonLd={[orgJsonLd(), websiteJsonLd()]}
      />
      {/* Most Popular Tests */}
      <section className="popular-tests" data-testid="popular-tests">
        <div className="container">
          <div className="section-icon">
            <img className="section-mark" src="/kovar_mark.png" alt="" />
          </div>
          <h1>Most Popular Tests</h1>
          <div className="products-grid">
            {popularProducts.map(product => (
              <ProductCard key={product._id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>{get('hero_title', 'Trusted Laboratory Testing of Anabolics, Peptides and More...')}</h1>
              <h3>{get('hero_subtitle', 'Harm reduction chemical analysis for anyone committed to their health and performance.')}</h3>
              <Link to="/services" className="btn-primary" data-testid="test-your-sample-btn">{get('hero_cta_label', 'Test your sample')}</Link>
            </div>
            <div className="hero-image">
              <img
                src="/img/vial-macro.jpg"
                alt="Laboratory sample analysis" loading="lazy" decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works" data-testid="how-it-works">
        <div className="container">
          <span className="how-it-works-label">{get('hiw_eyebrow', 'Fast And Reliable')}</span>
          <div className="how-it-works-inner">
            <div className="how-it-works-steps">
              {(get('hiw_steps', []) || []).map((step, i) => (
                <div key={i} className="step-card">
                  <div className="step-number">{step.num}</div>
                  <div className="step-title">{step.title}</div>
                  <div className="step-desc">{step.desc}</div>
                </div>
              ))}
            </div>
            <div className="how-it-works-image">
              <img
                src="/img/lab-scientist.jpg"
                alt="Kovar Analytics scientist performing HPLC peptide purity testing" loading="lazy" decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Choose Your Test */}
      <section className="choose-test" data-testid="choose-test">
        <div className="container">
          <div className="choose-test-inner">
            <div className="choose-test-left">
              <div className="section-icon" style={{ justifyContent: 'flex-start' }}>
                <img className="section-mark" src="/kovar_mark.png" alt="" />
              </div>
              <h1>{get('choose_test_title', 'Choose Your Test')}</h1>
              <p>
                {get('choose_test_body', "Did not find your test on the site?")} <Link to="/#contact-us" className="link-green">Write us</Link>
              </p>
              <Link to="/services" className="btn-outline" data-testid="all-tests-btn">All Tests</Link>
            </div>
            <div className="choose-test-center">
              <img
                src="/img/choose-test.png"
                alt="Browse the Kovar Analytics catalogue of peptide, anabolic steroid and SARM laboratory tests" loading="lazy" decoding="async"
              />
            </div>
            <div className="choose-test-right">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/c/${cat.slug}`}
                  className="category-link-item"
                  data-testid={`category-link-${cat.slug}`}
                >
                  <span>{cat.name}</span>
                  <svg className="arrow-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17" />
                  </svg>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Us Preview */}
      <section className="about-section" data-testid="about-preview">
        <div className="container">
          <div className="about-section-inner">
            <div className="about-section-left">
              <div className="section-icon" style={{ justifyContent: 'flex-start' }}>
                <img className="section-mark" src="/kovar_mark_white.png" alt="" />
              </div>
              <h1>About Us</h1>
              <h3>Pioneering Excellence in Chemical Analysis</h3>
              <p>
                At Kovar Analytics, we specialize in cutting-edge chemical analysis for steroids, peptides, and pharmaceutical compounds. With years of experience and state-of-the-art laboratory technology, we provide accurate, reliable, and confidential harm reduction chemical analysis for anyone committed to their health and performance.
              </p>
              <p>
                Our mission is simple - transparency, precision, and trust. Every sample is handled with strict protocols to ensure integrity, and our methods guarantee the highest quality standards.
              </p>
              <Link to="/about-us" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'white' }}>Read more</Link>
            </div>
            <div className="about-images">
              <img src="/img/lab-scientist.jpg" alt="Laboratory" loading="lazy" decoding="async" />
              <img src="/img/lab-equipment.jpg" alt="Laboratory" loading="lazy" decoding="async" />
              <img src="/img/vial-macro.jpg" alt="Laboratory" loading="lazy" decoding="async" />
              <img src="/img/hands-vial.jpg" alt="Laboratory" loading="lazy" decoding="async" />
              <img src="/img/lab-wide.jpg" alt="Laboratory" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <Features />

      {/* Reviews */}
      <ReviewsCarousel prefetchedReviews={reviews} />

      {/* Contact */}
      <ContactForm />
    </div>
  );
}

export default Home;
